/**
 * Disclosure — a section that expands and collapses.
 *
 * The largest measured drift left in the tree: 556 expand/collapse
 * implementations across 156 files, each owning its own boolean, its own
 * chevron rotation, and — almost always — no `aria-expanded` at all, which
 * means a screen reader is never told the section can open.
 *
 * Controlled or uncontrolled: pass `open` + `onOpenChange` to drive it, or
 * `defaultOpen` to let it keep its own state. Uncontrolled is the common case
 * and is why so many callers rolled their own — a component that forces you to
 * hold state for a purely visual toggle is more work than a useState.
 */
import React, { useId, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { FOCUS_RING } from '../mono/primitives';
import { type Tone } from './foundation';

export interface DisclosureProps {
  title: React.ReactNode;
  /** Mono eyebrow above the title. */
  label?: React.ReactNode;
  /** Right-aligned content in the summary row — a count, a status, a badge.
      Not for buttons: the whole summary row is the toggle. */
  meta?: React.ReactNode;
  children: React.ReactNode;

  /** Uncontrolled initial state. */
  defaultOpen?: boolean;
  /** Controlled state. Pass with onOpenChange. */
  open?: boolean;
  onOpenChange?: (next: boolean) => void;

  /** Coloured left edge for semantic state. */
  accent?: Tone;
  /** Hairline above the summary row — for a stack of disclosures. */
  divided?: boolean;
  disabled?: boolean;
  className?: string;
  bodyClassName?: string;
}

export function Disclosure({
  title,
  label,
  meta,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  accent,
  divided = false,
  disabled = false,
  className = '',
  bodyClassName = '',
}: DisclosureProps) {
  const [internal, setInternal] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internal;
  const panelId = useId();

  const toggle = () => {
    if (disabled) return;
    if (!isControlled) setInternal(!isOpen);
    onOpenChange?.(!isOpen);
  };

  return (
    <div
      className={`${divided ? 'border-t' : ''} ${className}`}
      style={{
        ...(divided ? { borderColor: 'var(--rule)' } : {}),
        ...(accent
          ? { boxShadow: `inset 3px 0 0 0 ${accent === 'neutral' ? 'var(--rule-bright)' : `var(--${accent})`}` }
          : {}),
      }}
    >
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
          disabled ? '' : 'hover:bg-[var(--hover-tint)]'
        } ${FOCUS_RING}`}
      >
        <ChevronRight
          aria-hidden="true"
          className={`h-3.5 w-3.5 shrink-0 text-[var(--text-dim)] transition-transform ${isOpen ? 'rotate-90' : ''}`}
        />
        <span className="min-w-0 flex-1">
          {label && (
            <span className="block font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">
              {label}
            </span>
          )}
          <span className="block text-sm text-[var(--text-pri)]">{title}</span>
        </span>
        {meta && <span className="shrink-0 text-sm text-[var(--text-ter)]">{meta}</span>}
      </button>

      {/* Unmounted when closed rather than hidden: these bodies routinely hold
          tables and forms, and keeping them mounted was measurably part of why
          pages with many sections were slow. */}
      {isOpen && (
        <div id={panelId} className={`px-4 pb-4 pl-10 ${bodyClassName}`}>
          {children}
        </div>
      )}
    </div>
  );
}

export interface DisclosureGroupProps {
  children: React.ReactNode;
  className?: string;
}

/** A ruled stack of Disclosures. Wrap in a Panel with `flush` for a bordered set. */
export function DisclosureGroup({ children, className = '' }: DisclosureGroupProps) {
  return (
    <div className={`[&>*+*]:border-t ${className}`} style={{ borderColor: 'var(--rule)' }}>
      {children}
    </div>
  );
}
